import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { ENTITLEMENTS, EntitlementsService } from 'app/services/entitlements.service';
import { combineLatest, firstValueFrom, Observable, of, Subject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { DialogFactoryService } from '../../dialog-factory.service';
import { DynamicDialogAction, DynamicDialogData } from '../../dynamic-dialog-modal/dynamic-dialog-modal.component';
import { DynamicDialogService } from '../../dynamic-dialog.service';
import { SchoolActivityInstance } from '../../models';
import { AttendanceRecord, AttendanceRecordState } from '../../models/AttendanceRecord';
import { AttendanceRecordService } from '../../services/attendance-record-service';
import {
	BulkSignUpForActivityReq,
	GetAttendeesForInstanceReq,
	SchoolActivityAttendee,
	SchoolActivityService,
} from '../../services/school-activity.service';
import { DeleteInstancePayload } from '../teacher-activity.component';

@Component({
	selector: 'sp-attendees',
	templateUrl: './attendees.component.html',
	styleUrls: ['../teacher-activity.component.scss'],
})
export class AttendeesComponent implements OnInit, OnChanges, OnDestroy {
	@ViewChild('addStudentsModalBody', { read: TemplateRef, static: false }) addStudentsModalBody!: TemplateRef<unknown>;

	@Input() selectedInstance: SchoolActivityInstance | undefined;
	@Input() maxNumAttending = 0;
	@Input() showDeleteInstance = true;
	@Output() openDeleteInstance: EventEmitter<DeleteInstancePayload> = new EventEmitter<DeleteInstancePayload>();

	loading = true;
	numAttendees = 0;
	numUnmarkedRecords = 0;
	addStudentDisabled = false;
	showAttendance = false;
	removeSubject$ = new Subject<number>();

	private destroy$ = new Subject<void>();
	private interval: ReturnType<typeof setInterval> | null = null;

	private addStudentsDialogService: DynamicDialogService | undefined;

	constructor(
		public activityService: SchoolActivityService,
		protected attendanceRecordService: AttendanceRecordService,
		private dialogFactoryService: DialogFactoryService,
		private entitlementsService: EntitlementsService
	) {}

	ngOnInit(): void {
		const attendanceEnabled = this.entitlementsService.isEntitlementEnabled$(ENTITLEMENTS.Attendance);

		if (attendanceEnabled) {
			this.showAttendance = this.showAttendanceCheckIn();
			this.interval = setInterval(() => {
				this.showAttendance = this.showAttendanceCheckIn();
			}, 1000);
		}

		this.removeSubject$.pipe(debounceTime(200), takeUntil(this.destroy$)).subscribe((studentId) => this.removeStudent(studentId));

		this.activityService.selectedStudents$.pipe(takeUntil(this.destroy$)).subscribe((results) => {
			this.addStudentsDialogService?.setDialogConfig({ disablePrimaryButton: !results });
		});

		if (this.selectedInstance?.id) {
			void firstValueFrom(this.attendanceRecordService.getRecordsForInstanceId(this.selectedInstance.id));

			this.attendanceRecordService
				.activityAttendance$(this.selectedInstance.id)
				.pipe(takeUntil(this.destroy$))
				.subscribe((records) => {
					this.numUnmarkedRecords = records.filter((record) => record.state === AttendanceRecordState.NotSelected).length;
				});

			this.loading = true;
			let attendanceRecords$: Observable<AttendanceRecord[]>;
			if (this.showAttendance && this.selectedInstance) {
				attendanceRecords$ = this.attendanceRecordService.getRecordsForInstanceId(this.selectedInstance.id);
			} else {
				attendanceRecords$ = of([]);
			}

			combineLatest([this.activityService.attendees$(this.selectedInstance.id), attendanceRecords$])
				.pipe(takeUntil(this.destroy$))
				.subscribe(([attendees, attendance]) => {
					this.numAttendees = attendees.length;
					this.refreshDisableButton();
					this.loading = false;
				});
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (this.selectedInstance?.current_num_attendees) {
			this.numAttendees = this.selectedInstance.current_num_attendees;
		}

		if (this.selectedInstance && this.selectedInstance.id > 0) {
			const req: GetAttendeesForInstanceReq = {
				activity_instance_id: this.selectedInstance.id,
				start_time: this.selectedInstance.start_time,
				include_user_profiles: true,
			};
			void this.activityService.GetAttendeesForInstance(req);
		}
		this.loading = false;
	}

	handleBack(): void {
		this.activityService.ClearSelectedActivityInstance();
	}

	handleDeleteInstance(event: MouseEvent, instance: SchoolActivityInstance): void {
		this.openDeleteInstance.emit({ event: event, instance: instance });
	}

	toggleSearch(): void {
		const data: DynamicDialogData = {
			headerText: 'Add Students to Activity',
			displayModalFooter: true,
			showCloseIcon: true,
			primaryButtonLabel: 'Add',
			secondaryButtonLabel: 'Cancel',
			disablePrimaryButton: true,
			modalBody: this.addStudentsModalBody,
			secondaryButtonGradientBackground: '#F0F2F5,#F0F2F5',
			secondaryButtonTextColor: '#7083A0',
			classes: 'tw-min-h-0',
		};
		this.addStudentsDialogService = this.dialogFactoryService.open(data, {
			panelClass: 'dynamic-dialog-modal-min',
			disableClose: false,
		});

		this.addStudentsDialogService.closed$.pipe(takeUntil(this.destroy$), filter(Boolean)).subscribe((result) => {
			const selectedOption: DynamicDialogAction = result as DynamicDialogAction;
			if (selectedOption === 'primary') {
				const students = this.activityService.selectedStudents$.getValue();
				const userIds = students.map((user) => user.id);
				if (userIds.length > 0 && this.selectedInstance) {
					const req: BulkSignUpForActivityReq = {
						user_ids: userIds,
						activity_id: this.selectedInstance.activity_id,
						scheduled_date: new Date(this.selectedInstance.start_time),
						activity_instance_id: this.selectedInstance.id,
					};
					const attendeeIds = this.activityService.attendeeIdsToDelete$.getValue();
					if (attendeeIds.length) {
						this.activityService.BulkRemoveAttendeeFromActivityInstanceAndSignUp(attendeeIds, req, this.selectedInstance);
					} else {
						this.activityService.TeacherBulkSignUpForActivity(req, this.selectedInstance);
					}
					this.activityService.selectedStudents$.next([]);
					this.activityService.attendeeIdsToDelete$.next([]);
				}
			}
		});
	}

	private refreshDisableButton(): void {
		this.addStudentDisabled = this.maxNumAttending > 0 && this.numAttendees >= this.maxNumAttending;
	}

	removeStudent(attendeeId: number): void {
		this.refreshDisableButton();
		this.activityService.RemoveAttendeeFromActivityInstanceAndSignUp(attendeeId);
	}

	markAllAsPresent(attendees: SchoolActivityAttendee[]): void {
		attendees.forEach((attendee) => {
			if (attendee.user && this.selectedInstance) {
				void firstValueFrom(this.attendanceRecordService.activityAttendanceForUser$(this.selectedInstance.id, attendee.user.id)).then(
					(attendanceRecord) => {
						if (attendanceRecord) {
							void firstValueFrom(
								this.attendanceRecordService.updateRecordState({
									id: attendanceRecord.id,
									state: 'present',
								})
							);
						}
					}
				);
			}
		});
	}

	ngOnDestroy(): void {
		if (this.interval) {
			clearInterval(this.interval);
		}
		this.destroy$.next(undefined);
		this.destroy$.complete();
	}

	showAttendanceCheckIn(): boolean {
		if (!this.selectedInstance) {
			return false;
		}
		const minutesBeforeStart = 5;
		const currentTime = new Date();
		const startTime = new Date(this.selectedInstance.start_time);
		const endTime = new Date(this.selectedInstance.end_time);
		startTime.setMinutes(startTime.getMinutes() - minutesBeforeStart);
		return currentTime >= startTime && currentTime <= endTime;
	}
}
